import { useState } from 'react'
import { UserCheck } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useUnmatchedC2BTransactions } from '@/hooks/use-payments'
import { formatKES } from '@/lib/utils'
import { AssignC2BPaymentModal } from './assign-c2b-payment-modal'
import type { UnmatchedC2BTransaction } from '@riverpark/types'

function formatDateTime(unixSeconds: number): string {
  return new Date(unixSeconds * 1000).toLocaleString('en-KE', {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  })
}

export function UnmatchedC2BTab() {
  const { data, isLoading } = useUnmatchedC2BTransactions()
  const [assignTarget, setAssignTarget] = useState<UnmatchedC2BTransaction | null>(null)
  const rows = data?.data ?? []

  return (
    <div className="space-y-4">
      <p className="font-sans text-sm text-muted-foreground">
        Direct Paybill payments Safaricom confirmed but whose account number didn't match any plot —
        assign each to the right member to settle it.
      </p>

      {isLoading ? (
        <p className="font-sans text-sm text-muted-foreground">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="rounded border border-border-2 px-4 py-6 text-center font-sans text-sm text-muted-foreground">
          No unmatched payments — everything's reconciled.
        </p>
      ) : (
        <>
          {/* Mobile */}
          <div className="space-y-2 sm:hidden">
            {rows.map((r) => (
              <div key={r.id} className="rounded border border-border-2 bg-card p-3">
                <div className="flex items-center justify-between gap-3">
                  <span className="font-sans text-sm font-medium text-ink">
                    {formatKES(r.amount)}
                  </span>
                  <Button variant="secondary" size="sm" onClick={() => setAssignTarget(r)}>
                    <UserCheck size={14} className="mr-1.5" />
                    Assign
                  </Button>
                </div>
                <p className="mt-1 font-sans text-xs text-muted-foreground">
                  Acc. {r.billRefNumber || '—'} · {r.payerName || 'Unknown payer'}
                  {r.phone && ` · ${r.phone}`} · {formatDateTime(r.createdAt)}
                </p>
              </div>
            ))}
          </div>

          {/* Desktop */}
          <div className="hidden overflow-x-auto rounded border border-border-2 sm:block">
            <table className="w-full font-sans text-sm">
              <thead>
                <tr className="border-b border-border-2 bg-cream-2 text-left text-label uppercase tracking-[0.08em] text-muted-foreground">
                  <th className="px-4 py-3">Account entered</th>
                  <th className="px-4 py-3">Payer</th>
                  <th className="px-4 py-3">Phone</th>
                  <th className="px-4 py-3">Amount</th>
                  <th className="px-4 py-3">Received</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-border-2 last:border-0">
                    <td className="px-4 py-3 text-ink">{r.billRefNumber || '—'}</td>
                    <td className="px-4 py-3 text-ink">{r.payerName || 'Unknown payer'}</td>
                    <td className="px-4 py-3 text-ink">{r.phone || '—'}</td>
                    <td className="px-4 py-3 font-medium text-ink">{formatKES(r.amount)}</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {formatDateTime(r.createdAt)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button variant="secondary" size="sm" onClick={() => setAssignTarget(r)}>
                        <UserCheck size={14} className="mr-1.5" />
                        Assign
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {assignTarget && (
        <AssignC2BPaymentModal transaction={assignTarget} onClose={() => setAssignTarget(null)} />
      )}
    </div>
  )
}
